/** Spec §14.2 page 3: daily cap, bedtime window, breaks. Preview runs the real evaluator. */
import { useState } from "react";

import { useCreatePolicy, usePolicies, usePolicyPreview } from "../api/hooks.js";
import { Empty, PageState } from "../components/PageState.js";

const TYPES = [
  "DEVICE_PLAYTIME_DAILY", "GAME_PLAYTIME_DAILY", "BEDTIME_WINDOW",
  "SCHOOL_HOMEWORK_BLACKOUT", "BREAK_AFTER_CONTINUOUS_USE", "APP_DENYLIST",
] as const;

export function SchedulesPage({ deviceId }: { deviceId: string }) {
  const policies = usePolicies(deviceId);
  const create = useCreatePolicy(deviceId);
  const preview = usePolicyPreview(deviceId);
  const [type, setType] = useState<string>(TYPES[0]!);
  const [minutes, setMinutes] = useState("60");
  const [bedStart, setBedStart] = useState("21:30");
  const [bedEnd, setBedEnd] = useState("06:30");
  const [usageMin, setUsageMin] = useState("75");

  if (!deviceId) return <Empty label="No device selected." />;

  const parameters = () =>
    type === "BEDTIME_WINDOW" || type === "SCHOOL_HOMEWORK_BLACKOUT"
      ? { window: { start: bedStart, end: bedEnd } }
      : type.startsWith("DEVICE_PLAYTIME")
        ? { minutes: Number(minutes) }
        : type === "BREAK_AFTER_CONTINUOUS_USE"
          ? { enforce_after_minutes: Number(minutes), warn_after_minutes: Math.max(1, Number(minutes) - 5) }
          : { package_ids: ["com.example.game"] };

  return (
    <PageState query={{ isPending: policies.isPending, error: policies.error }}>
      <section className="stack">
        <h3>Saved schedules</h3>
        {(policies.data ?? []).length === 0 ? (
          <p className="notice">No policies yet.</p>
        ) : (
          <table>
            <thead><tr><th>Type</th><th>Priority</th><th>Rev</th><th>Enabled</th><th>Parameters</th></tr></thead>
            <tbody>
              {(policies.data ?? []).map((p) => (
                <tr key={p.id}>
                  <td>{p.type}</td><td>{p.priority}</td><td>{p.revision}</td>
                  <td>{p.enabled ? "yes" : "no"}</td>
                  <td className="mono">{JSON.stringify(p.parameters)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ type, parameters: parameters(), enabled: true });
        }}
      >
        <h3>New schedule</h3>
        <label htmlFor="ptype">Type</label>
        <select id="ptype" value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {type.includes("PLAYTIME") || type.includes("BREAK") ? (
          <>
            <label htmlFor="mins">Minutes</label>
            <input id="mins" type="number" min={1} max={1440} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </>
        ) : null}
        {type === "BEDTIME_WINDOW" ? (
          <>
            <label htmlFor="bedStart">Bedtime start (local)</label>
            <input id="bedStart" type="time" value={bedStart} onChange={(e) => setBedStart(e.target.value)} />
            <label htmlFor="bedEnd">Wake time (local)</label>
            <input id="bedEnd" type="time" value={bedEnd} onChange={(e) => setBedEnd(e.target.value)} />
          </>
        ) : null}
        <button disabled={create.isPending}>{create.isPending ? "Saving…" : "Save policy"}</button>
        {create.data ? <p className="notice">Saved as revision {create.data.revision}.</p> : null}
        {create.error ? <p className="notice danger">{create.error.message}</p> : null}
      </form>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          preview.mutate({ now: new Date().toISOString(), usage_today: { daily_total_minutes: Number(usageMin) } });
        }}
      >
        <h3>Evaluator preview (no enforcement claim)</h3>
        <label htmlFor="usage">Simulated playtime today (minutes)</label>
        <input id="usage" type="number" min={0} max={1440} value={usageMin} onChange={(e) => setUsageMin(e.target.value)} />
        <button disabled={preview.isPending}>Preview</button>
        {preview.data ? (
          <p className="notice">
            decision: {preview.data.decision ?? "DEFAULT_ALLOW"} · reason: {preview.data.reason} ·{" "}
            {preview.data.enforcement_state ? `state ${preview.data.enforcement_state}` : "no enforcement state"}
          </p>
        ) : null}
      </form>
    </PageState>
  );
}
